//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
// network-offline imports
import { DeterioratingClock } from "@components/svgs/DeterioratingClock";
import { styles } from "./NetworkOffline.styles";
// styles
import spacing from "@styles/spacing.styles";
// material-ui
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import withStyles from "@material-ui/core/styles/withStyles";
import { WithStyles } from "@material-ui/core/styles/withStyles";

///////////////
// CONSTANTS //
///////////////

export const MAX_TIME_TO_RETRY = 6 * 60 * 60 * 1000; // 6 hours

//////////////////
// TYPINGS
//////////////////
export interface IProps extends WithStyles<typeof styles> {
  onRetry?: () => Promise<any>;
  additionalText?: JSX.Element | string | null;
  initialNetworkFailureScale: number;
}

export interface IState {
  timeToRetry: number;
  lastTimeToRetry: number;
  isRetrying: boolean;
}

//////////////////
// COMPONENT
//////////////////
class NetworkOfflineCmpt extends React.Component<IProps, IState> {
  public static defaultProps = {
    initialNetworkFailureScale: 0,
  };

  private interval: ReturnType<typeof setInterval>;

  constructor(props: IProps) {
    super(props);

    // initial TTR will be at least 15s, + 1-45s via RNG
    const timeToRetry = Math.min(
      MAX_TIME_TO_RETRY,
      Math.round(15 + Math.random() * 45) * Math.pow(2, props.initialNetworkFailureScale),
    );

    this.state = {
      timeToRetry,
      lastTimeToRetry: timeToRetry,
      isRetrying: false,
    };
  }

  private retry = () => {
    if (this.state.isRetrying) {
      return;
    }

    const newTimeToRetry = Math.min(this.state.lastTimeToRetry * 2, MAX_TIME_TO_RETRY / 1000);

    if (this.props.onRetry) {
      const afterRetry = () => setTimeout(() => this.setState({ isRetrying: false }), 3000); // 3s grace till we allow another retry

      this.props.onRetry().then(afterRetry).catch(afterRetry);
    }

    this.setState({
      timeToRetry: newTimeToRetry,
      lastTimeToRetry: newTimeToRetry,
      isRetrying: !!this.props.onRetry,
    });
  };

  public componentDidMount() {
    if (this.props.onRetry) {
      this.interval = setInterval(() => {
        const timeToRetry = this.state.timeToRetry - 1;

        if (timeToRetry === -1) {
          this.retry();
        } else {
          this.setState({
            timeToRetry,
          });
        }
      }, 1000);

      // initial TTR will be at least 15s, + 1-45s via RNG
      const timeToRetry = Math.min(
        MAX_TIME_TO_RETRY,
        Math.round(15 + Math.random() * 45) * Math.pow(2, this.props.initialNetworkFailureScale),
      );

      this.setState({
        timeToRetry,
        lastTimeToRetry: timeToRetry,
        isRetrying: false,
      });
    }
  }

  public componentWillUnmount() {
    clearInterval(this.interval);
  }

  public render() {
    const { additionalText, onRetry } = this.props;
    const { timeToRetry } = this.state;
    let retryText = "please check your network settings.";
    if (onRetry) {
      let timeText: string;
      let minutes: number;
      let hours: number;
      let days: number;
      let divisionCount = 0;
      divisionCount = divisionCount + ((minutes = Math.floor(timeToRetry / 60)) > 0 ? 1 : 0);
      divisionCount = divisionCount + ((hours = Math.floor(minutes / 60)) > 0 ? 1 : 0);
      divisionCount = divisionCount + ((days = Math.floor(hours / 24)) > 0 ? 1 : 0);
      switch (divisionCount) {
        case 0:
          timeText = timeToRetry + (timeToRetry !== 1 ? " seconds" : " second");
          break;
        case 1:
          timeText = minutes + (minutes !== 1 ? " minutes" : " minute");
          break;
        case 2:
          timeText = hours + (hours !== 1 ? " hours" : " hour");
          break;
        default:
          timeText = days + (days !== 1 ? " days" : " day");
      }

      retryText = `We'll be retrying in ${timeText}.`;
    }

    let statusText: string;

    if (this.state.isRetrying) {
      statusText = "Retrying now...";
    } else {
      statusText = `Oops, we're having trouble connecting to Clockwise right now. ${retryText}`;
    }

    return (
      <div className={this.props.classes.container}>
        <DeterioratingClock style={{ marginTop: 43 }} />
        <Typography variant="h6" style={{ marginTop: 18 }}>
          Connection Error
        </Typography>
        <Typography variant="body1" style={{ padding: spacing.lg }}>
          {statusText}
        </Typography>
        {additionalText ? (
          <Typography variant="body1" style={{ padding: spacing.lg, paddingTop: 0 }}>
            {additionalText}
          </Typography>
        ) : null}
        {onRetry ? (
          <React.Fragment>
            <Button
              disabled={this.state.isRetrying}
              className={this.props.classes.button}
              onClick={this.retry}
              variant="contained"
            >
              Retry Now
            </Button>
          </React.Fragment>
        ) : null}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const NetworkOffline = withStyles(styles)(NetworkOfflineCmpt);
